/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useRef, useState } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TestimonialsCarousel.module.scss';

type Testimonial = {
  quote: string;
  logo: string;
  logoAlt: string;
  logoWidth: number;
  logoHeight: number;
  name: string;
  role: string;
  initials: string;
  avatarColor: string;
};

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable — a registry-sourced record we can stand behind when a decision is questioned. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
    logo: '/storage/img/customers/quantifind.svg',
    logoAlt: 'Quantifind',
    logoWidth: 180,
    logoHeight: 38,
    name: 'Adam Mulliken',
    role: 'CPO, Quantifind',
    initials: 'AM',
    avatarColor: '#4a5cff',
  },
  {
    quote:
      "We use Global Database's credit risk data when verifying and onboarding customers. It gives our teams the company and financial information they need to assess risk, complete checks and make decisions with confidence.",
    logo: '/storage/img/customers/kroll.svg',
    logoAlt: 'Kroll',
    logoWidth: 137,
    logoHeight: 32,
    name: 'Carol Zhang',
    role: 'Associate Vice President, Kroll',
    initials: 'CZ',
    avatarColor: '#3bb56b',
  },
  {
    quote:
      'We use Global Database to enrich our bank account validation and payee checks with business data. It helps us identify the company behind the payment and gives our customers more confidence before they pay.',
    logo: '/storage/img/customers/trustpair.svg',
    logoAlt: 'Trustpair',
    logoWidth: 120,
    logoHeight: 27,
    name: 'Alexandre Outman',
    role: 'Lead Product Manager, Trustpair',
    initials: 'AO',
    avatarColor: '#2b7fd4',
  },
  {
    quote:
      'Global Database helps us build a clear picture of our addressable market across the UK and Ireland. With more than 100 criteria available, we can segment companies precisely and map the right opportunities to each of our teams.',
    logo: '/storage/img/customers/schneider.svg',
    logoAlt: 'Schneider Electric',
    logoWidth: 160,
    logoHeight: 32,
    name: 'Cameron Trayner',
    role: 'Schneider Electric',
    initials: 'CT',
    avatarColor: '#ff8a3d',
  },
  {
    quote:
      "Global Database's shareholder data is an important part of our intelligence graph. It helps us connect individuals to the businesses they own or control, revealing relationships that conventional screening can miss and giving our clients a more complete view of who they are dealing with.",
    logo: '/storage/img/customers/yoono.svg',
    logoAlt: 'YOONO',
    logoWidth: 140,
    logoHeight: 32,
    name: 'Oli Warriner',
    role: 'Chief Technology Officer, YOONO',
    initials: 'OW',
    avatarColor: '#7c3aed',
  },
  {
    quote:
      'Our products depend on authoritative company information across multiple jurisdictions. The government registry data provided by Global Database helps us enrich our offering and deliver reliable business information across key European markets.',
    logo: '/storage/img/customers/experian.svg',
    logoAlt: 'Experian',
    logoWidth: 140,
    logoHeight: 32,
    name: 'Wendy Song',
    role: 'BDC Director, Experian',
    initials: 'WS',
    avatarColor: '#e0455f',
  },
];

type Props = {
  title?: string;
  description?: string;
  primaryCtaLabel?: string;
  primaryCtaRoute?: string;
  secondaryCtaLabel?: string;
};

export const TestimonialsCarousel = ({
  title,
  description,
  primaryCtaLabel,
  primaryCtaRoute = '/customers',
  secondaryCtaLabel,
}: Props) => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();
  const trackRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const toggleExpand = (index: number) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const scrollByDirection = (direction: 1 | -1) => {
    const node = trackRef.current;
    if (!node) {
      return;
    }
    const card = node.querySelector(`.${styles.card}`) as HTMLElement | null;
    const step = (card?.offsetWidth ?? 400) + 40;
    node.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <TitleBlock
          align="left"
          title={title ?? t("What it's like to work with us")}
          description={description ?? t('Real results from companies who made the switch')}
          fullWidth
          className={styles.title}
        />

        <div ref={trackRef} className={styles.track}>
          {TESTIMONIALS.map((item, index) => {
            const isExpanded = expanded[index];
            return (
              <article key={index} className={styles.card}>
                <div className={styles.quoteCard}>
                  <div className={styles.logo}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={item.logo}
                      alt={item.logoAlt}
                      width={item.logoWidth}
                      height={item.logoHeight}
                      loading="lazy"
                    />
                  </div>

                  <p className={cn(styles.quote, isExpanded && styles.quoteExpanded)}>{`“${item.quote}”`}</p>

                  <button type="button" className={styles.readMore} onClick={() => toggleExpand(index)}>
                    {isExpanded ? 'Read less' : 'Read more'}
                  </button>
                </div>

                <div className={styles.author}>
                  <span className={styles.avatar} style={{ backgroundColor: item.avatarColor }}>
                    {item.initials}
                  </span>
                  <div className={styles.authorInfo}>
                    <strong className={styles.name}>{item.name}</strong>
                    <p className={styles.role}>{item.role}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className={styles.footer}>
          <div className={styles.actions}>
            <Button type="dark" size="xl" route={primaryCtaRoute}>
              {primaryCtaLabel ?? t('Read more stories')}
            </Button>

            <span className={styles.or}>or</span>

            {secondaryNavigation?.book_demo?.slug && (
              <Button type="dark-outline" size="xl" route={secondaryNavigation.book_demo.slug}>
                {secondaryCtaLabel ?? t('Request a demo')}
              </Button>
            )}
          </div>

          <div className={styles.arrows}>
            <button
              type="button"
              className={styles.arrow}
              onClick={() => scrollByDirection(-1)}
              aria-label="Previous testimonial"
            >
              <Icon type="arrow-1" />
            </button>
            <button
              type="button"
              className={styles.arrow}
              onClick={() => scrollByDirection(1)}
              aria-label="Next testimonial"
            >
              <Icon type="arrow-3" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
